/**
 * ID Prefixes - Sistema di tipizzazione via prefisso
 *
 * REGOLA FONDAMENTALE: Nessun campo "type" - il tipo è determinato dal prefisso ID.
 *
 * Formato: {prefix}-{timestamp}-{random}
 * Esempio: prj-1703001234567-a3b2c
 */

// =============================================================================
// PREFIX DEFINITIONS
// =============================================================================

export const ID_PREFIXES = {
  // Core
  acc: 'acc', // Account/Workspace (tenant)
  usr: 'usr', // Utente
  cnt: 'cnt', // Contatto
  org: 'org', // Organizzazione

  // Multi-Tenant
  dom: 'dom', // Account Domain
  sub: 'sub', // Subscription
  mbr: 'mbr', // Account Member

  // Projects
  prj: 'prj', // Progetto
  tsk: 'tsk', // Task
  tkt: 'tkt', // Ticket
  tme: 'tme', // Time Entry
  mst: 'mst', // Milestone
  lst: 'lst', // Task list/grouping

  // Communications
  cnv: 'cnv', // Conversazione
  msg: 'msg', // Messaggio generico
  eml: 'eml', // Email
  eth: 'eth', // Email thread
  att: 'att', // Allegato
  cal: 'cal', // Chiamata VoIP
  vcl: 'vcl', // Videochiamata
  pcs: 'pcs', // Provider session

  // Calendar
  evt: 'evt', // Calendar event
  evr: 'evr', // Event reminder

  // Accounting
  inv: 'inv', // Fattura gestionale
  ein: 'ein', // Fattura elettronica payload
  sdi: 'sdi', // Notifica SDI
  coa: 'coa', // Piano dei conti
  jrn: 'jrn', // Movimento contabile
  jln: 'jln', // Riga contabile
  mov: 'mov', // Movement bancario

  // Banking
  bnk: 'bnk', // Conto bancario
  btx: 'btx', // Transazione bancaria
  rcn: 'rcn', // Riconciliazione

  // Other
  qte: 'qte', // Preventivo
  pay: 'pay', // Pagamento
  due: 'due', // Scadenza
  tag: 'tag', // Tag
  doc: 'doc', // Documento
  not: 'not', // Nota
  aud: 'aud', // Evento audit
  rle: 'rle', // Regola/Automazione
  ntf: 'ntf', // Notifica
  chk: 'chk', // Chunk semantico
  emb: 'emb', // Embedding
  lnk: 'lnk', // Link tra entità

  // Config
  apk: 'apk', // API Key
  cfg: 'cfg', // Provider config
  pas: 'pas', // Project accounting snapshot
  prp: 'prp', // Invoice draft proposal

  // Chat
  cht: 'cht', // Chat session

  // Timer
  tmr: 'tmr', // Timer session

  // Files
  fil: 'fil', // Project file

  // Comments
  cmt: 'cmt', // Comment
  cth: 'cth', // Comment thread

  // Settings
  upr: 'upr', // User profile
  tcf: 'tcf', // Team config
  npr: 'npr', // Notification preferences
  icf: 'icf', // Integration config

  // Templates
  tpl: 'tpl', // Project template

  // AI Project Manager
  ctr: 'ctr', // Contract
  pml: 'pml', // Payment Milestone
  aia: 'aia', // AI Analysis
  pfa: 'pfa', // Portfolio Analysis
  aal: 'aal', // AI Activity Log

  // Support System
  log: 'log', // Client log entry
  sup: 'sup', // Support request
} as const;

export type IdPrefix = keyof typeof ID_PREFIXES;

// =============================================================================
// ID GENERATION
// =============================================================================

/**
 * Genera un ID con prefisso tipizzato
 */
export function genId(prefix: IdPrefix): string {
  const timestamp = Date.now();
  const random = Math.random().toString(36).slice(2, 7);
  return `${prefix}-${timestamp}-${random}`;
}

/**
 * Estrae il prefisso da un ID
 */
export function getPrefix(id: string): IdPrefix | null {
  const prefix = id.split('-')[0];
  return prefix in ID_PREFIXES ? (prefix as IdPrefix) : null;
}

/**
 * Verifica se un ID ha un certo prefisso
 */
export function hasPrefix(id: string, prefix: IdPrefix): boolean {
  return id.startsWith(`${prefix}-`);
}

/**
 * Ottiene il nome del tipo da un ID
 */
export function getTypeName(id: string): string | null {
  const prefix = getPrefix(id);
  if (!prefix) return null;

  const typeNames: Record<IdPrefix, string> = {
    acc: 'Account',
    usr: 'User',
    cnt: 'Contact',
    org: 'Organization',
    dom: 'AccountDomain',
    sub: 'Subscription',
    mbr: 'AccountMember',
    prj: 'Project',
    tsk: 'Task',
    tme: 'TimeEntry',
    mst: 'Milestone',
    cnv: 'Conversation',
    msg: 'Message',
    eml: 'Email',
    eth: 'EmailThread',
    att: 'Attachment',
    cal: 'Call',
    vcl: 'VideoCall',
    pcs: 'ProviderSession',
    inv: 'Invoice',
    ein: 'EInvoice',
    sdi: 'SdiNotification',
    coa: 'ChartOfAccounts',
    jrn: 'JournalEntry',
    jln: 'JournalLine',
    mov: 'Movement',
    bnk: 'BankAccount',
    btx: 'BankTransaction',
    rcn: 'Reconciliation',
    qte: 'Quote',
    pay: 'Payment',
    due: 'DueDate',
    tag: 'Tag',
    doc: 'Document',
    not: 'Note',
    aud: 'AuditEvent',
    rle: 'Rule',
    ntf: 'Notification',
    chk: 'Chunk',
    emb: 'Embedding',
    lnk: 'Link',
    apk: 'ApiKey',
    cfg: 'ProviderConfig',
    pas: 'ProjectAccountingSnapshot',
    prp: 'InvoiceDraftProposal',
    cht: 'ChatSession',
    lst: 'TaskList',
    tmr: 'TimerSession',
    fil: 'ProjectFile',
    cmt: 'Comment',
    cth: 'CommentThread',
    evt: 'CalendarEvent',
    evr: 'EventReminder',
    upr: 'UserProfile',
    tcf: 'TeamConfig',
    npr: 'NotificationPreferences',
    icf: 'IntegrationConfig',
    tpl: 'ProjectTemplate',
    ctr: 'Contract',
    pml: 'PaymentMilestone',
    aia: 'AIAnalysis',
    pfa: 'PortfolioAnalysis',
    aal: 'AIActivityLog',
    log: 'ClientLog',
    sup: 'SupportRequest',
  };

  return typeNames[prefix];
}

// =============================================================================
// TYPE HELPERS
// =============================================================================

/** Branded type per ID tipizzati */
export type TypedId<P extends IdPrefix> = string & { __prefix: P };

/** Crea un ID tipizzato */
export function typedId<P extends IdPrefix>(prefix: P): TypedId<P> {
  return genId(prefix) as TypedId<P>;
}

/** Tipo per ProjectId */
export type ProjectId = TypedId<'prj'>;
/** Tipo per TaskId */
export type TaskId = TypedId<'tsk'>;
/** Tipo per TicketId */
export type TicketId = TypedId<'tkt'>;
/** Tipo per ContactId */
export type ContactId = TypedId<'cnt'>;
/** Tipo per InvoiceId */
export type InvoiceId = TypedId<'inv'>;
/** Tipo per TimeEntryId */
export type TimeEntryId = TypedId<'tme'>;
/** Tipo per ConversationId */
export type ConversationId = TypedId<'cnv'>;
/** Tipo per MessageId */
export type MessageId = TypedId<'msg'>;
/** Tipo per UserId */
export type UserId = TypedId<'usr'>;
/** Tipo per MilestoneId */
export type MilestoneId = TypedId<'mst'>;
/** Tipo per TaskListId */
export type TaskListId = TypedId<'lst'>;
/** Tipo per CalendarEventId */
export type CalendarEventId = TypedId<'evt'>;
/** Tipo per BankAccountId */
export type BankAccountId = TypedId<'bnk'>;
/** Tipo per BankTransactionId */
export type BankTransactionId = TypedId<'btx'>;
/** Tipo per ReconciliationId */
export type ReconciliationId = TypedId<'rcn'>;
/** Tipo per PaymentId */
export type PaymentId = TypedId<'pay'>;
/** Tipo per ChartOfAccountsId */
export type ChartOfAccountsId = TypedId<'coa'>;
/** Tipo per UserProfileId */
export type UserProfileId = TypedId<'upr'>;
/** Tipo per TeamConfigId */
export type TeamConfigId = TypedId<'tcf'>;
/** Tipo per NotificationPreferencesId */
export type NotificationPreferencesId = TypedId<'npr'>;
/** Tipo per IntegrationConfigId */
export type IntegrationConfigId = TypedId<'icf'>;
/** Tipo per ProjectTemplateId */
export type ProjectTemplateId = TypedId<'tpl'>;
/** Tipo per ContractId */
export type ContractId = TypedId<'ctr'>;
/** Tipo per PaymentMilestoneId */
export type PaymentMilestoneId = TypedId<'pml'>;
/** Tipo per AIAnalysisId */
export type AIAnalysisId = TypedId<'aia'>;
/** Tipo per PortfolioAnalysisId */
export type PortfolioAnalysisId = TypedId<'pfa'>;
/** Tipo per AIActivityLogId */
export type AIActivityLogId = TypedId<'aal'>;
/** Tipo per VideoCallMeetingId */
export type VideoCallMeetingId = TypedId<'vcl'>;
/** Tipo per AccountDomainId */
export type AccountDomainId = TypedId<'dom'>;
/** Tipo per SubscriptionId */
export type SubscriptionTypeId = TypedId<'sub'>;
/** Tipo per AccountMemberId */
export type AccountMemberId = TypedId<'mbr'>;

// =============================================================================
// VALIDATION
// =============================================================================

/**
 * Asserts that an object does not have a "type" field
 *
 * RULE: Objects are typed by their ID prefix, not by a type field
 */
export function assertNoTypeField(record: unknown): void {
  if (!record || typeof record !== 'object') return;
  if ('type' in (record as object)) {
    throw new Error('Campo `type` vietato: gli oggetti sono tipizzati dall\'id.');
  }
}
